import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaChartLine, FaArrowUp, FaArrowDown, FaShieldAlt, FaExclamationTriangle, FaSync, FaCalendarAlt, FaRupeeSign, FaChartBar, FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp, FaIndustry, FaInfoCircle, FaRocket, FaLeaf, FaSeedling, FaStar, FaFire, FaCrown, FaTag, FaBriefcase, FaMoneyBillWave, FaLock, FaSignInAlt, FaPhone, FaEnvelope, FaEye, FaTimes, FaFilePdf, FaExternalLinkAlt, FaDownload, FaGoogle, FaArrowRight
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const LongTermGrowthPortfolio = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ LongTermGrowthPortfolio: 0 });



    // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'IPO Research & Analysis',
    description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page5`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.LongTermGrowthPortfolio === 1) {
      fetchPageMetadata(); // Add this line
    }
  }, [isLoggedIn, checkingLogin, contentAccess.LongTermGrowthPortfolio]);
  // Metadata End===========================================================


  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ LongTermGrowthPortfolio: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ LongTermGrowthPortfolio: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ LongTermGrowthPortfolio: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================
  
  // ==================== ORIGINAL COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'recoDate', direction: 'desc' }); // Default sort by Reco Date descending (newest first)
  const [filter, setFilter] = useState('all');
  const [selectedStock, setSelectedStock] = useState(null);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [processedData, setProcessedData] = useState({ growthStocks: [], researchReports: [], googleForm: null, disclosures: [] });

  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1bUB8qs_v1vCspaTBmk7PR6GyaoGRrlB8LrRiUqLMxfg/values/MM%20LONG%20TERM%20GROWTH%20PORTFOLIO?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.LongTermGrowthPortfolio === 1) {
      fetchPortfolioData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.LongTermGrowthPortfolio]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.LongTermGrowthPortfolio === 1) {
      const intervalId = setInterval(() => {
        fetchPortfolioData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.LongTermGrowthPortfolio]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
      setColumnHeaders(result.columnHeaders);
    }
  }, [data]);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching portfolio data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Helper function to safely parse dates
  const safeParseDate = (dateValue) => {
    if (!dateValue || dateValue === 'N/A' || dateValue === '') return new Date(0);
    
    // Convert to string if it's not already
    let dateStr = String(dateValue).trim();
    
    // Try to parse common date formats
    const parts = dateStr.split(/[/-]/);
    if (parts.length === 3) {
      // Assume DD/MM/YYYY format (most common in India)
      let day = parseInt(parts[0]);
      let month = parseInt(parts[1]) - 1;
      let year = parseInt(parts[2]);
      
      // Check if year is 2-digit
      if (parts[2].length === 2) {
        year = 2000 + year;
      }
      
      // Validate if this is a valid date
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
      
      // Try MM/DD/YYYY format
      month = parseInt(parts[0]) - 1;
      day = parseInt(parts[1]);
      year = parseInt(parts[2]);
      if (parts[2].length === 2) year = 2000 + year;
      const date2 = new Date(year, month, day);
      if (!isNaN(date2.getTime())) {
        return date2;
      }
    }
    
    // Try direct Date parsing
    const directDate = new Date(dateStr);
    if (!isNaN(directDate.getTime())) {
      return directDate;
    }
    
    return new Date(0);
  };

  // Enhanced Dynamic data processing - Fixed to properly parse Reco Price
  const processData = (values) => {
    if (!values) {
      console.log('No data available for processing');
      return { growthStocks: [], researchReports: [], googleForm: null, disclosures: [], columnHeaders: [] };
    }

    let growthStocks = [];
    let researchReports = [];
    let googleForm = null;
    let disclosures = [];
    let headers = [];
    let headerRowIndex = -1;
    
    // FIRST PASS: Process ALL rows to find Google Forms and Research Reports
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      // Check for Google Form link - Look in ALL columns
      let formLink = null;
      let formTitle = null;
      
      for (let col = 0; col < row.length; col++) {
        const cell = row[col];
        if (cell && typeof cell === 'string') {
          // Check for Google Form link
          if (cell.includes('forms.gle') || cell.includes('form')) {
            formLink = cell;
          }
          // Check for form title
          if (cell.includes('FILL THIS') || cell.includes('FORM') || cell.includes('Google Form')) {
            formTitle = cell;
          }
        }
      }
      
      if (formLink) {
        console.log('Found Google Form link at row', i, ':', formLink);
        googleForm = {
          title: formTitle || 'Google Feedback Form',
          link: formLink
        };
        continue;
      }
      
      // Check for research reports (Google Drive links)
      for (let col = 0; col < row.length; col++) {
        const cell = row[col];
        if (cell && typeof cell === 'string' && cell.includes('drive.google.com')) {
          const companyName = row[0] ? row[0].toString().trim() : '';
          const reportLink = cell;
          
          if (companyName && reportLink && 
              !companyName.includes('COMING SOON') && 
              !companyName.includes('S.NO') &&
              !companyName.includes('TICKER') &&
              companyName.length > 2) {
            
            const isDuplicate = researchReports.some(r => r.companyName === companyName);
            if (!isDuplicate) {
              researchReports.push({
                companyName: companyName,
                reportLink: reportLink,
                reportDate: new Date().toLocaleDateString(),
                additionalInfo: row[1] || row[2] || row[3] || ''
              });
              console.log('Added research report:', companyName);
            }
          }
          break;
        }
      }
    }
    
    // SECOND PASS: Find stock data table
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      // Find the stock data table header
      const hasSNO = row.some(cell => cell && cell.toString().trim().includes('S.NO'));
      const hasTicker = row.some(cell => cell && cell.toString().trim().includes('TICKER'));
      const hasStockName = row.some(cell => cell && (
        cell.toString().trim().includes('STOCK NAME') || 
        cell.toString().trim().includes('STOCK NAME')
      ));
      
      if ((hasSNO && hasTicker) || (hasSNO && hasStockName)) {
        headerRowIndex = i;
        headers = row.map(h => h ? h.toString().trim() : '');
        
        console.log('Found headers at row', i, ':', headers);
        
        // Process stock rows after header
        for (let j = i + 1; j < values.length; j++) {
          const stockRow = values[j];
          if (!stockRow || stockRow.length === 0) continue;
          
          // Skip "COMING SOON" or completely empty rows
          const firstCell = stockRow.find(cell => cell && cell.toString().trim() !== '');
          if (!firstCell || firstCell.toString().includes('COMING SOON')) {
            continue;
          }
          
          // Stop if we find another header row
          if (stockRow.some(cell => cell && cell.toString().includes('S.NO') && 
              stockRow.some(cell2 => cell2 && cell2.toString().includes('IPO')))) {
            console.log('Found next section, stopping at row', j);
            break;
          }
          
          // Skip rows that contain Google Drive links or Google Forms
          let hasDriveLink = false;
          for (let col = 0; col < stockRow.length; col++) {
            const cell = stockRow[col];
            if (cell && typeof cell === 'string' && 
                (cell.includes('drive.google.com') || cell.includes('forms.gle'))) {
              hasDriveLink = true;
              break;
            }
          }
          if (hasDriveLink) {
            continue;
          }
          
          // Check if this is a data row (should have S.NO as a number)
          const snoValue = stockRow[2] || '';
          const sno = parseInt(snoValue.toString().trim());
          if (isNaN(sno) || sno < 1) continue;
          
          // Check if row has any valid data
          const hasValidData = stockRow.some((cell, idx) => {
            if (idx < headers.length && headers[idx]) {
              return cell !== undefined && cell !== null && cell.toString().trim() !== '';
            }
            return false;
          });
          
          if (!hasValidData) continue;
          
          // Create complete stock object with ALL columns
          const stockObj = {};
          let hasData = false;
          let isNew = false;
          
          // Map ALL columns
          headers.forEach((header, index) => {
            if (header) {
              const rawValue = stockRow[index] ? stockRow[index].toString().trim() : '';
              
              // Check for "New" column
              if (header.toLowerCase().includes('new')) {
                isNew = rawValue.toLowerCase().includes('new');
                stockObj[header] = rawValue;
              }
              // Store S.NO for ID
              else if (header.includes('S.NO') || header.includes('NO.')) {
                stockObj[header] = sno;
                stockObj.id = sno;
              }
              // Parse numeric values
              else if ((header.includes('PRICE') || header.includes('CMP') || 
                       header.includes('RETURN') || header.includes('RECO') ||
                       header.includes('VALUE') || header.includes('AMOUNT') ||
                       header.includes('QTY') || header.includes('QUANTITY') ||
                       header.includes('TARGET') || header.includes('STOPLOSS')) &&
                       rawValue !== '') {
                const cleanValue = rawValue.replace(/[^0-9.-]/g, '').trim();
                const numValue = parseFloat(cleanValue);
                stockObj[header] = isNaN(numValue) ? rawValue : numValue;
              }
              // Parse percentage values
              else if ((header.includes('%') || header.includes('PERCENT') || 
                       header.includes('RETURN')) && rawValue !== '') {
                const cleanValue = rawValue.replace(/[^0-9.-]/g, '').trim();
                const numValue = parseFloat(cleanValue);
                stockObj[header] = isNaN(numValue) ? rawValue : numValue;
              }
              // Store other values as-is
              else {
                stockObj[header] = rawValue;
              }
              
              if ((header.includes('TICKER') && rawValue) || 
                  (header.includes('STOCK') && rawValue) ||
                  (header.includes('NAME') && rawValue && !header.includes('IPO'))) {
                hasData = true;
              }
            }
          });
          
          if (hasData && sno) {
            // Extract key values
            const ticker = stockObj['TICKER'] || stockObj['Ticker'] || stockObj['Symbol'] || '';
            const stockName = stockObj['STOCK NAME'] || stockObj['STOCK NAME'] || 
                             stockObj['Company Name'] || stockObj['COMPANY'] || 
                             stockObj['Stock Name'] || '';
            
            // Find important columns
            const recoPriceHeader = headers.find(h => 
              (h && (h.includes('RECO') || h.includes('Reco')) && h.includes('PRICE')) || 
              h.includes('Recommendation Price') ||
              h.includes('Entry Price') ||
              h === 'Reco Price'
            );
            
            const cmpHeader = headers.find(h => 
              h && (h.includes('CMP') || 
              h.includes('Current Price') ||
              h.includes('LTP') ||
              h.includes('Market Price'))
            );
            
            const returnHeader = headers.find(h => 
              h && (h.includes('RETURN') || 
              h.includes('Return') ||
              (h.includes('%') && !h.includes('Date')))
            );
            
            const dateHeader = headers.find(h => 
              h && (h.includes('DATE') || 
              h.includes('Date') ||
              h.includes('RECO DATE'))
            );
            
            const target1Header = headers.find(h => 
              h && (h.includes('TARGET 1') || h.includes('Target 1') || h === 'TARGET 1')
            );
            
            const target2Header = headers.find(h => 
              h && (h.includes('TARGET 2') || h.includes('Target 2') || h === 'TARGET 2')
            );
            
            const stoplossHeader = headers.find(h => 
              h && (h.includes('STOPLOSS') || h.includes('Stop Loss') || h === 'STOPLOSS')
            );
            
            const recoPrice = recoPriceHeader ? parseFloat(stockObj[recoPriceHeader]) || 0 : 0;
            const cmp = cmpHeader ? parseFloat(stockObj[cmpHeader]) || 0 : 0;
            const returnPercentage = returnHeader ? parseFloat(stockObj[returnHeader]) || 0 : 0;
            const recoDate = dateHeader ? (stockObj[dateHeader] || '') : '';
            const target1 = target1Header ? stockObj[target1Header] || '' : '';
            const target2 = target2Header ? stockObj[target2Header] || '' : '';
            const stoploss = stoplossHeader ? stockObj[stoplossHeader] || '' : '';
            
            const absoluteReturn = cmp - recoPrice;
            const sector = getSectorFromName(stockName);
            
            growthStocks.push({
              id: sno,
              ticker: ticker,
              stockName: stockName,
              recoPrice: recoPrice,
              cmp: cmp,
              returnPercentage: returnPercentage,
              recoDate: recoDate,
              target1: target1,
              target2: target2,
              stoploss: stoploss,
              absoluteReturn: absoluteReturn,
              isPositive: returnPercentage > 0,
              sector: sector,
              isNew: isNew,
              dynamicData: stockObj,
              columnHeaders: headers,
              hasDynamicData: true,
              originalIndex: j // Preserve original order
            });
          }
        }
        break; // Stop after finding first stock table
      }
    }
    
    // THIRD PASS: Process disclosures (only after we've collected everything else)
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      if (row.length > 0 && row[0]) {
        const text = row[0].toString();
        
        // Skip research reports and Google forms
        const lowerText = text.toLowerCase();
        if (lowerText.includes('market maestroo reseach report') ||
            lowerText.includes('research report') ||
            lowerText.includes('fill this google form') ||
            lowerText.includes('http') ||
            lowerText.includes('drive.google.com') ||
            lowerText.includes('forms.gle')) {
          continue;
        }
        
        if (text.includes('SEBI REGISTERED')) {
          disclosures.push({ type: 'registration', text: text });
        } else if (text.includes('RISK DISCLOSURE')) {
          disclosures.push({ type: 'risk', text: text });
        } else if (text.includes('DISCLAIMER')) {
          disclosures.push({ type: 'disclaimer', text: text });
        } else if (text.includes('INVESTOR CAUTION')) {
          disclosures.push({ type: 'caution', text: text });
        }
      }
    }
    
    console.log(`Processed ${growthStocks.length} stocks with ${headers.length} columns each`);
    console.log(`Research reports: ${researchReports.length}, Google Form: ${googleForm ? 'Found: ' + googleForm.title : 'Not found'}`);
    
    return { growthStocks, researchReports, googleForm, disclosures, columnHeaders: headers };
  };

  const getSectorFromName = (companyName) => {
    const name = companyName.toLowerCase();
    
    if (name.includes('pharma') || name.includes('lab') || name.includes('health') || name.includes('medical')) {
      return 'Pharmaceuticals';
    } else if (name.includes('bank') || name.includes('finance') || name.includes('credit')) {
      return 'Banking & Finance';
    } else if (name.includes('infra') || name.includes('construct') || name.includes('build')) {
      return 'Infrastructure';
    } else if (name.includes('tech') || name.includes('software') || name.includes('it')) {
      return 'Technology';
    } else if (name.includes('auto') || name.includes('motor') || name.includes('vehicle')) {
      return 'Automobile';
    } else if (name.includes('oil') || name.includes('gas') || name.includes('petro')) {
      return 'Oil & Gas';
    } else if (name.includes('power') || name.includes('energy') || name.includes('electric')) {
      return 'Power & Energy';
    } else if (name.includes('finserv') || name.includes('insurance')) {
      return 'Financial Services';
    } else {
      return 'Diversified';
    }
  };

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    dateString = String(dateString).trim();

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString.replace(/-/g, '/');
    }

    if (/^\d{7,8}$/.test(dateString)) {
      if (dateString.length === 8) {
        const day = dateString.substring(0, 2);
        const month = dateString.substring(2, 4);
        const year = dateString.substring(4, 8);
        if (parseInt(day) <= 31 && parseInt(month) <= 12) {
          return `${day}/${month}/${year}`;
        }
      } else if (dateString.length === 7) {
        const day = '0' + dateString.substring(0, 1);
        const month = dateString.substring(1, 3);
        const year = dateString.substring(3, 7);
        if (parseInt(day) <= 31 && parseInt(month) <= 12) {
          return `${day}/${month}/${year}`;
        }
      }
    }

    return dateString;
  };

  // Get ALL dynamic fields for display
  const getAllDisplayableFields = (stock) => {
    if (!stock.dynamicData) return [];
    
    const alreadyDisplayed = [
      'S.NO', 'TICKER', 'STOCK NAME', 'STOCK NAME',
      'RECO PRICE', 'CMP', 'RETURN', 'RECO DATE',
      'Reco Price', 'CMP', 'RETURN', 'Reco Date',
      'Company Name', 'SYMBOL', 'Ticker',
      'id', 'ticker', 'stockName', 'recoPrice', 'cmp', 
      'returnPercentage', 'recoDate', 'absoluteReturn', 
      'isPositive', 'sector', 'dynamicData', 'columnHeaders',
      'hasDynamicData', 'isNew', 'target1', 'target2', 'stoploss',
      'New', 'TIMEFRAME'
    ];
    
    const allFields = [];
    
    Object.entries(stock.dynamicData).forEach(([key, value]) => {
      const keyUpper = key.toUpperCase();
      const isExcluded = alreadyDisplayed.some(excluded => 
        keyUpper.includes(excluded.toUpperCase())
      );
      
      if (!isExcluded && 
          value !== undefined && 
          value !== null && 
          value.toString().trim() !== '' &&
          !(typeof value === 'number' && value === 0)) {
        
        const displayName = formatHeaderName(key);
        allFields.push({
          key,
          displayName,
          value,
          isNumeric: typeof value === 'number',
          isPercentage: keyUpper.includes('%') || keyUpper.includes('RETURN') || keyUpper.includes('PERCENT'),
          isCurrency: (keyUpper.includes('PRICE') || keyUpper.includes('VALUE') || 
                      keyUpper.includes('AMOUNT') || keyUpper.includes('CMP')) && 
                      typeof value === 'number'
        });
      }
    });
    
    return allFields;
  };

  // Format header names for display
  const formatHeaderName = (header) => {
    return header
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format value for display
  const formatValue = (field) => {
    if (field.isNumeric) {
      if (field.isPercentage) {
        return `${field.value >= 0 ? '+' : ''}${field.value.toFixed(2)}%`;
      } else if (field.isCurrency) {
        return `₹${field.value.toFixed(2)}`;
      } else {
        return field.value % 1 === 0 ? field.value.toString() : field.value.toFixed(2);
      }
    }
    return field.value.toString();
  };

  // Reset to default sort (Reco Date descending)
  const resetToDefaultSort = () => {
    setSortConfig({ key: 'recoDate', direction: 'desc' });
  };

  const filteredAndSortedStocks = useMemo(() => {
    let filtered = [...processedData.growthStocks];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.ticker.toLowerCase().includes(term) ||
        stock.stockName.toLowerCase().includes(term) ||
        stock.sector.toLowerCase().includes(term)
      );
    }
    
    if (filter === 'gainers') {
      filtered = filtered.filter(stock => stock.returnPercentage > 0);
    } else if (filter === 'losers') {
      filtered = filtered.filter(stock => stock.returnPercentage < 0);
    }
    
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Special handling for date sorting
        if (sortConfig.key === 'recoDate') {
          aValue = safeParseDate(aValue);
          bValue = safeParseDate(bValue);
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Preserve original order when no sort is applied
      filtered.sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0));
    }
    
    return filtered;
  }, [processedData.growthStocks, searchTerm, filter, sortConfig]);

  const portfolioStats = useMemo(() => {
    const growthStocks = processedData.growthStocks;
    if (growthStocks.length === 0) return null;
    
    const totalInvested = growthStocks.reduce((sum, stock) => sum + stock.recoPrice, 0);
    const currentValue = growthStocks.reduce((sum, stock) => sum + stock.cmp, 0);
    const totalReturn = growthStocks.reduce((sum, stock) => sum + stock.absoluteReturn, 0);
    const totalReturnPercentage = ((currentValue - totalInvested) / totalInvested) * 100;
    const avgReturn = growthStocks.reduce((sum, stock) => sum + stock.returnPercentage, 0) / growthStocks.length;
    const positiveStocks = growthStocks.filter(s => s.returnPercentage > 0).length;
    const negativeStocks = growthStocks.filter(s => s.returnPercentage < 0).length;
    
    return {
      totalStocks: growthStocks.length,
      totalInvested,
      currentValue,
      totalReturn,
      totalReturnPercentage,
      avgReturn,
      positiveStocks,
      negativeStocks
    };
  }, [processedData.growthStocks]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Long-Term Growth Portfolio.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Long-Term Growth Portfolio</h1>
                    <p className="text-blue-100">Real-time stock recommendations with verified performance data</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.LongTermGrowthPortfolio !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Long-Term Growth Portfolio yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Long-Term Growth Portfolio</h1>
                    <p className="text-blue-100">Real-time stock recommendations with verified performance data</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content access restricted.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Long-Term Growth Portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Portfolio</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchPortfolioData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                <p className="text-blue-100">{pageMetadata.description}</p>
                <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <FaShieldAlt className="text-yellow-300" />
                  <span className="font-semibold">SEBI INH000021757</span>
                </div>
                <button
                  onClick={fetchPortfolioData}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaSync className="text-sm" />
                  Refresh Data
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Search and Filter Controls */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by stock name, ticker, or sector..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    filter === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  All Stocks
                </button>
                <button
                  onClick={() => setFilter('gainers')}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    filter === 'gainers' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  <FaArrowUp />
                  Gainers
                </button>
                <button
                  onClick={() => setFilter('losers')}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    filter === 'losers' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  <FaArrowDown />
                  Losers
                </button>
              </div>
            </div>

            {/* Sort Controls */}
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex-wrap">
              <span className="text-gray-600 font-medium">Sort by:</span>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleSort('recoDate')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'recoDate' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Recommended Date
                  {sortConfig.key === 'recoDate' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('returnPercentage')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'returnPercentage' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Return %
                  {sortConfig.key === 'returnPercentage' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('cmp')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'cmp' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Current Price
                  {sortConfig.key === 'cmp' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('absoluteReturn')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'absoluteReturn' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Absolute Return
                  {sortConfig.key === 'absoluteReturn' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
              </div>
              {sortConfig.key && sortConfig.key !== 'recoDate' && (
                <button
                  onClick={resetToDefaultSort}
                  className="ml-auto text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <FaCalendarAlt className="text-xs" />
                  Reset to Date Sort
                </button>
              )}
              {sortConfig.key === 'recoDate' && sortConfig.direction === 'desc' && (
                <div className="ml-auto text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
                  <FaCalendarAlt className="text-xs" />
                  Newest First
                </div>
              )}
              <div className="ml-auto text-sm text-gray-500">
                {filteredAndSortedStocks.length} stocks found
                {columnHeaders.length > 0 && ` • ${columnHeaders.length} columns`}
              </div>
            </div>
          </div>

          {/* Stock Cards - Shows ALL columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
           

            {filteredAndSortedStocks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                <FaSeedling className="text-gray-400 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Stocks Found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setFilter('all');
                  }}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6">
                {filteredAndSortedStocks.map((stock, index) => {
                  const dynamicFields = getAllDisplayableFields(stock);
                  
                  return (
                    <motion.div
                      key={stock.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ y: -4 }}
                      className="group relative"
                    >
                      {/* New Badge */}
                      {stock.isNew && (
                        <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24">
                          <div 
                            className="absolute top-10 transform -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold py-1 px-6 shadow-lg"
                            style={{ right: "-57px", top: "18px" }}
                          >
                            NEW
                          </div>
                        </div>
                      )}

                      {/* Stock Card */}
                      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 transition-all duration-300 overflow-hidden hover:border-blue-300 hover:shadow-xl h-full">
                        {/* {stock.returnPercentage > 10 && (
                          <div className="absolute top-4 right-4 z-10">
                            <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                              <FaCrown className="text-xs" />
                              Top Performer
                            </div>
                          </div>
                        )} */}

                        <div className="p-5">
                          {/* Stock Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900 text-lg line-clamp-1 group-hover:text-blue-600 transition-colors">
                                {stock.stockName}
                              </h3>
                              <div className="flex items-center mt-1">
                                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                  {stock.ticker}
                                </span>
                                {/* <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                  {stock.sector}
                                </span> */}
                              </div>
                            </div>
                          </div>

                          {/* Core Metrics Section */}
                          <div className="mb-4">
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">Current Price</div>
                                <div className="font-bold text-gray-900 text-lg">₹{stock.cmp}</div>
                              </div>
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <div className="text-xs text-blue-600 mb-1">Reco Price</div>
                                <div className="font-bold text-blue-700 text-lg">₹{stock.recoPrice}</div>
                              </div>
                            </div>

                            {/* Return Metrics */}
                            <div className={`p-3 rounded-lg ${stock.isPositive ? 'bg-green-50' : 'bg-red-50'}`}>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Total Return</span>
                                <div className={`text-lg font-bold ${stock.isPositive ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                                  {stock.isPositive ? <FaArrowUp className="text-sm" /> : <FaArrowDown className="text-sm" />}
                                  {stock.isPositive ? '+' : ''}{stock.returnPercentage}%
                                </div>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                Absolute: ₹{stock.absoluteReturn >= 0 ? '+' : ''}{Math.abs(stock.absoluteReturn).toFixed(2)}
                              </div>
                            </div>
                          </div>

                          {/* Target & Stop Loss Section */}
                          {(stock.target1 || stock.target2 || stock.stoploss) && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="grid grid-cols-3 gap-2">
                                {stock.target1 && (
                                  <div className="bg-green-50 p-2 rounded-lg text-center">
                                    <div className="text-xs text-green-700 font-medium">Target 1</div>
                                    <div className="font-bold text-green-800">₹{stock.target1}</div>
                                  </div>
                                )}
                                {stock.target2 && (
                                  <div className="bg-green-50 p-2 rounded-lg text-center">
                                    <div className="text-xs text-green-700 font-medium">Target 2</div>
                                    <div className="font-bold text-green-800">₹{stock.target2}</div>
                                  </div>
                                )}
                                {stock.stoploss && (
                                  <div className="bg-red-50 p-2 rounded-lg text-center">
                                    <div className="text-xs text-red-700 font-medium">Stop Loss</div>
                                    <div className="font-bold text-red-800">₹{stock.stoploss}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* ALL Dynamic Fields Section */}
                          {dynamicFields.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">Additional Data</h4>
                                <span className="text-xs text-gray-500">
                                  {dynamicFields.length} fields
                                </span>
                              </div>
                              
                              <div className="space-y-3">
                                {dynamicFields.map((field, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`p-2.5 rounded-lg ${field.isPercentage || field.isCurrency ? 'bg-blue-50' : 'bg-gray-50'}`}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="text-xs font-medium text-gray-600 mb-0.5">
                                          {field.displayName}
                                        </div>
                                        <div className={`text-sm font-bold ${
                                          field.isPercentage 
                                            ? field.value >= 0 ? 'text-green-600' : 'text-red-600'
                                            : field.isCurrency ? 'text-blue-700' : 'text-gray-900'
                                        }`}>
                                          {formatValue(field)}
                                        </div>
                                      </div>
                                      {field.isPercentage && (
                                        <div className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                          field.value >= 0 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {field.value >= 0 ? '+' : ''}{field.value.toFixed(1)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Recommendation Date */}
                          {stock.recoDate && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-gray-600">
                                  <FaCalendarAlt className="text-gray-400 text-xs" />
                                  <span>Recommended Date</span>
                                </div>
                                <span className="font-medium text-gray-900">{formatDate(stock.recoDate)}</span>
                              </div>
                            </div>
                          )}

                          {/* Footer */}
                          {/* <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Stock ID: {stock.id}</span>
                              <span className={`px-2 py-0.5 rounded-full ${
                                stock.returnPercentage > 20 ? 'bg-green-100 text-green-800' :
                                stock.returnPercentage > 0 ? 'bg-blue-100 text-blue-800' :
                                stock.returnPercentage > -10 ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {stock.returnPercentage > 20 ? 'Strong Buy' :
                                 stock.returnPercentage > 0 ? 'Buy' :
                                 stock.returnPercentage > -10 ? 'Hold' : 'Review'}
                              </span>
                            </div>
                          </div> */}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Research Reports Section - Display only if there are research reports */}
          {processedData.researchReports.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-12"
            >
              <div className="bg-gradient-to-r from-white to-blue-50 rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <FaFilePdf className="text-red-300 text-2xl" />
                    Research Reports
                    <span className="text-sm bg-white/20 px-2 py-1 rounded-full ml-2">
                      {processedData.researchReports.length} Reports
                    </span>
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    In-depth analysis and research reports from Market Maestroo
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {processedData.researchReports.map((report, index) => {
                      const getDriveFileId = (url) => {
                        const patterns = [
                          /\/d\/([a-zA-Z0-9_-]+)/,
                          /id=([a-zA-Z0-9_-]+)/,
                          /file\/d\/([a-zA-Z0-9_-]+)/
                        ];
                        for (const pattern of patterns) {
                          const match = url.match(pattern);
                          if (match) return match[1];
                        }
                        return null;
                      };
                      
                      const fileId = getDriveFileId(report.reportLink);
                      const thumbnailUrl = fileId ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w200` : null;
                      
                      return (
                        <motion.a
                          key={index}
                          href={report.reportLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          whileHover={{ y: -4, scale: 1.02 }}
                          className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                        >
                          <div className="relative h-32 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                            {thumbnailUrl ? (
                              <img 
                                src={thumbnailUrl} 
                                alt={report.companyName}
                                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `
                                    <div className="flex items-center justify-center h-full">
                                      <div className="text-center">
                                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                        </svg>
                                        <p className="text-xs text-gray-500">PDF Document</p>
                                      </div>
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                  <FaFilePdf className="w-12 h-12 text-red-400 mx-auto mb-2" />
                                  <p className="text-xs text-gray-500">PDF Report</p>
                                </div>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <span className="bg-white/90 text-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg">
                                <FaExternalLinkAlt className="text-blue-600" />
                                View Report
                              </span>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <h4 className="font-bold text-gray-900 text-base mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {report.companyName}
                            </h4>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <FaCalendarAlt className="text-gray-400" />
                                <span>{report.reportDate}</span>
                              </div>
                              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1">
                                <FaDownload className="text-xs" />
                                Open PDF
                              </span>
                            </div>
                            {report.additionalInfo && (
                              <p className="text-xs text-gray-500 mt-2 line-clamp-1">
                                {report.additionalInfo}
                              </p>
                            )}
                          </div>
                        </motion.a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Google Form Section - Display only if Google Form exists */}
          {processedData.googleForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-12"
            >
              <div className="bg-gradient-to-r from-purple-50 to-purple-50 rounded-2xl border border-purple-200 overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-purple-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <FaGoogle className="text-white text-2xl" />
                      {processedData.googleForm.title}
                    </h3>
                  </div>
                  <div>
                    <a
                      href={processedData.googleForm.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex-shrink-0"
                    >
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-500 hover:from-purple-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-3"
                      >
                        <FaGoogle className="text-lg" />
                        Fill Google Form
                        <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Disclosures Section */}
          {processedData.disclosures.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12"
            >
              <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FaShieldAlt className="text-blue-500" />
                  Regulatory Disclosures
                </h3>
                <div className="space-y-6">
                  {processedData.disclosures.map((disclosure, index) => {
                    let title = '';
                    let icon = null;
                    let color = 'text-gray-700';
                    
                    if (disclosure.type === 'registration') {
                      title = 'SEBI Registration';
                      icon = <FaShieldAlt className="text-blue-500" />;
                      color = 'text-blue-700';
                    } else if (disclosure.type === 'risk') {
                      title = 'Risk Disclosure';
                      icon = <FaExclamationTriangle className="text-amber-500" />;
                      color = 'text-amber-700';
                    } else if (disclosure.type === 'disclaimer') {
                      title = 'Disclaimer';
                      icon = <FaInfoCircle className="text-purple-500" />;
                      color = 'text-purple-700';
                    } else if (disclosure.type === 'caution') {
                      title = 'Investor Caution';
                      icon = <FaExclamationTriangle className="text-red-500" />;
                      color = 'text-red-700';
                    }
                    
                    return (
                      <div key={index} className={`${color} bg-white/50 p-5 rounded-xl border border-gray-100`}>
                        <div className="flex items-start gap-3">
                          <div className="mt-1">{icon}</div>
                          <div>
                            <h4 className="font-bold mb-2">{title}</h4>
                            <p className="text-sm leading-relaxed opacity-90 whitespace-pre-line">
                              {disclosure.text.replace(/\r\n/g, '\n')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            {lastUpdated && (
              <p className="mt-1">Last refreshed: {lastUpdated.toLocaleString()}</p>
            )}
            <p className="mt-2 text-xs bg-gray-100 inline-block px-3 py-1 rounded-full">
              Market Maestroo Private Limited | SEBI Registration No: INH000021757 | Long-Term Growth Portfolio
            </p>
            {columnHeaders.length > 0 && (
              <div className="mt-3 text-xs text-gray-400" />
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default LongTermGrowthPortfolio;